import React, { useEffect, useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/card';
import { Heading } from '@/components/ui/shared/heading';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Loader2, SlashIcon, CheckIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import useFetch from 'use-http';
import { useUserState } from '@/components/context/UserContext';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { Command, CommandItem, CommandList } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import LineChart from '@/components/shared/LineChart';

const LaboratoryResearchDetails = () => {
  const location = useLocation();
  const {
    selectedResearchId,
    groupResearchIds,
    data: initialData,
  } = location.state || {};

  const [data, setData] = useState(initialData || null);
  const [allData, setAllData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { get: getResearch } = useFetch('/UserAccount/LabaratoryResearch');
  const userState = useUserState();

  const [selectedCharts, setSelectedCharts] = useState([]);
  const [showCharts, setShowCharts] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const ids = groupResearchIds?.length
        ? groupResearchIds
        : [selectedResearchId];
      const query = userState.espbiEsi
        ? `?ids=${ids.join('&ids=')}&espbiEsi=${userState.espbiEsi}`
        : `?ids=${ids.join('&ids=')}`;

      const result = await getResearch(query);
      if (Array.isArray(result)) {
        const matched = result.find((r) => r.externalId === selectedResearchId);
        if (matched) {
          setData(matched);
          setSelectedCharts([]);
          setAllData(result);
        }
      }
      setIsLoading(false);
    };

    fetchData();
  }, [getResearch, selectedResearchId, groupResearchIds, userState.espbiEsi]);

  const toggleChart = (chart) => {
    setSelectedCharts((prev) =>
      prev.includes(chart) ? prev.filter((c) => c !== chart) : [...prev, chart],
    );
  };

  const chartOptions = useMemo(() => {
    const allObservations = allData.flatMap((r) =>
      (r.observations || []).map((obs) => ({
        ...obs,
        orderDate: r.orderDate,
      })),
    );
    const names = allObservations.map((o) => o.name).filter(Boolean);
    return [...new Set(names)];
  }, [allData]);

  useEffect(() => {
    setShowCharts(chartOptions.length === 1); // auto-show if only 1
    if (chartOptions.length === 1) {
      setSelectedCharts(chartOptions);
    }
  }, [chartOptions]);

  if (!data) {
    return (
      <div className="p-4">
        <p>Laboratorinio tyrimo duomenys nerasti.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Breadcrumb className="px-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/userAccount/laboratory-researches">
                Laboratoriniai tyrimai
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator>
            <SlashIcon />
          </BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage>{data.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card>
        <CardHeader>
          <Heading variant="userAccountDetailsTitle">Rezultatai</Heading>
        </CardHeader>
        <CardContent className="space-y-2">
          <div>
            <span>Data: </span>
            <span>{format(new Date(data.orderDate), 'yyyy-MM-dd')}</span>
          </div>
          <div>
            <span>Pavadinimas: </span>
            <span>{data.name}</span>
          </div>
          <div>
            <span>Būsena: </span>
            <span>{data.statusDisplay}</span>
          </div>
          <div className="pt-2 font-semibold uppercase">Rezultatai:</div>

          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span>Kraunama...</span>
            </div>
          ) : Array.isArray(data.observations) &&
            data.observations.length > 0 ? (
            data.observations.map((obj, idx) => {
              const referenceRange = obj.referenceRange;
              const value = obj.value;
              const isOutOfRange = (() => {
                if (!referenceRange || !value) return false;
                const match = referenceRange.match(
                  /^\(?([\d.]+) - ([\d.]+)\)?$/,
                );
                if (match) {
                  const min = parseFloat(match[1]);
                  const max = parseFloat(match[2]);
                  return parseFloat(value) < min || parseFloat(value) > max;
                }
                return false;
              })();

              return (
                <div key={idx}>
                  <span>{obj.name}: </span>
                  <span
                    className={isOutOfRange ? 'text-red-600 font-medium' : ''}
                  >
                    {value || '-'}
                  </span>
                  <span>{obj.unit && ` ${obj.unit}`}</span>, Norma:{' '}
                  <span>{referenceRange || '-'}</span>
                </div>
              );
            })
          ) : (
            <div>Rezultatų nerasta</div>
          )}
        </CardContent>
        <CardFooter />
      </Card>

      <Card>
        <CardHeader>
          <Heading variant="userAccountDetailsTitle">Grafikai</Heading>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span>Kraunama...</span>
            </div>
          ) : (
            <>
              {chartOptions.length > 1 && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <Popover>
                    <PopoverTrigger>
                      <Button
                        variant="outline"
                        className="w-full sm:w-[300px] justify-start text-left"
                      >
                        {selectedCharts.length > 0
                          ? `Pasirinkimai: ${selectedCharts.length}`
                          : 'Pasirinkite...'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                      <Command>
                        <CommandList>
                          <CommandItem
                            onSelect={() => {
                              if (
                                selectedCharts.length === chartOptions.length
                              ) {
                                setSelectedCharts([]);
                              } else {
                                setSelectedCharts(chartOptions);
                              }
                            }}
                            className="cursor-pointer font-semibold text-primary"
                          >
                            <div className="flex items-center justify-between w-full">
                              <span>Visi</span>
                              {selectedCharts.length ===
                                chartOptions.length && (
                                <CheckIcon className="w-4 h-4" />
                              )}
                            </div>
                          </CommandItem>

                          {chartOptions.map((chart) => (
                            <CommandItem
                              key={chart}
                              onSelect={() => toggleChart(chart)}
                              className="cursor-pointer"
                            >
                              <div className="flex items-center justify-between w-full">
                                <span>{chart}</span>
                                {selectedCharts.includes(chart) && (
                                  <CheckIcon className="w-4 h-4 text-primary" />
                                )}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Button
                    onClick={() => setShowCharts(true)}
                    disabled={selectedCharts.length === 0}
                  >
                    Rodyti
                  </Button>
                </div>
              )}

              {showCharts &&
                selectedCharts.map((chartName) => {
                  const allObservations = allData.flatMap((r) =>
                    (r.observations || []).map((obs) => ({
                      ...obs,
                      orderDate: r.orderDate,
                    })),
                  );
                  const filtered = allObservations.filter(
                    (o) =>
                      o.name === chartName &&
                      !isNaN(parseFloat(o.value)) &&
                      isFinite(o.value),
                  );
                  if (!filtered.length) return null;

                  const sorted = [...filtered].sort(
                    (a, b) => new Date(a.orderDate) - new Date(b.orderDate),
                  );

                  return (
                    <div key={chartName}>
                      <LineChart
                        dataName={`${chartName} (${sorted[0].unit || ''})`}
                        dataSet={sorted.map((o) => parseFloat(o.value))}
                        xValues={sorted.map((o) => {
                          const rawDate =
                            o.orderDate || o.resultDate || o.observationDate;
                          const date = new Date(rawDate);
                          return isNaN(date.getTime())
                            ? 'Nežinoma data'
                            : format(date, 'yyyy-MM-dd');
                        })}
                      />
                    </div>
                  );
                })}
            </>
          )}
        </CardContent>
        <CardFooter />
      </Card>
    </div>
  );
};

export default LaboratoryResearchDetails;
